import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { TableauResource, TableauResourceListResponse } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  CalendarClock,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { TableauScheduleDialog } from './TableauScheduleDialog';

// Tableau Web UI 와 같은 순서: 숫자/ASCII 먼저, 한글 마지막. numeric 정렬로 00,02,99 자연 순서.
const COLLATOR = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

interface Props {
  dataSourceId: string;
}

export function TableauResources({ dataSourceId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'tableau-resources', dataSourceId],
    queryFn: () => api.tableauResourcesList(dataSourceId),
    // sync 또는 어떤 자원의 integrate 가 진행 중일 때만 polling. idle 에선 mutation invalidation 으로 충분.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return 3000;
      if (d.syncRunning) return 3000;
      if (d.resources.some((r) => r.integrateStartedAt)) return 3000;
      return false;
    },
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scheduleOpenFor, setScheduleOpenFor] = useState<TableauResource | null>(null);
  const [descBuffer, setDescBuffer] = useState<string>('');
  const [descDirty, setDescDirty] = useState(false);
  const [query, setQuery] = useState('');

  const resources = data?.resources ?? [];
  const metadataApiAvailable = data?.metadataApiAvailable ?? false;

  // 1) removedAt 필터링
  // 2) Tableau UI 와 같은 순서로 정렬 (numeric/ASCII 먼저, 한글 마지막)
  const sorted = useMemo(() => {
    const all = resources.filter((r) => !r.removedAt);
    return [...all].sort((a, b) => {
      const byPath = COLLATOR.compare(a.projectPath, b.projectPath);
      if (byPath !== 0) return byPath;
      return COLLATOR.compare(a.name, b.name);
    });
  }, [resources]);

  // 3) 검색 필터 (case-insensitive, name OR projectPath substring)
  const trimmedQuery = query.trim();
  const visible = useMemo(() => {
    if (trimmedQuery.length === 0) return sorted;
    const q = trimmedQuery.toLowerCase();
    return sorted.filter(
      (r) => r.name.toLowerCase().includes(q) || r.projectPath.toLowerCase().includes(q),
    );
  }, [sorted, trimmedQuery]);

  const selected = visible.find((r) => r.id === selectedId) ?? visible[0] ?? null;

  // 자원 변경 시 description buffer reset
  const onSelect = (r: TableauResource) => {
    setSelectedId(r.id);
    setDescBuffer(r.description ?? '');
    setDescDirty(false);
  };

  const integrateMutation = useMutation({
    mutationFn: ({ r, attemptLive }: { r: TableauResource; attemptLive?: boolean }) =>
      api.tableauResourceIntegrate(dataSourceId, r.id, attemptLive ? { attemptLive } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tableau-resources', dataSourceId] });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: ({ r, cron }: { r: TableauResource; cron: string | null }) =>
      api.tableauResourceSchedule(dataSourceId, r.id, { cron }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tableau-resources', dataSourceId] });
    },
  });

  const descMutation = useMutation({
    mutationFn: ({ r, description }: { r: TableauResource; description: string | null }) =>
      api.tableauResourceDescription(dataSourceId, r.id, { description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tableau-resources', dataSourceId] });
      setDescDirty(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (r: TableauResource) => api.tableauResourceDelete(dataSourceId, r.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tableau-resources', dataSourceId] });
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources'] });
      setSelectedId(null);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => api.dataSourceSync(dataSourceId, { scope: 'structure' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tableau-resources', dataSourceId] });
    },
  });

  // 자동 sync 는 의도적으로 없앰: 신규 ds 등록은 create 엔드포인트가 이미 enqueueDataSourceSyncStructure
  // 를 호출해 1회 sync 가 자동으로 시작됨. 실패한 sync 를 새로고침으로 자동 재시도하는 일도 막아야 한다
  // (사용자는 명시적으로 "동기화" 버튼을 눌러야 재시도).

  return (
    <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-foreground">
            자원 ({visible.length}
            {trimmedQuery.length > 0 && visible.length !== sorted.length
              ? ` / 전체 ${sorted.length}`
              : ''}
            )
          </h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            title="자원 카탈로그 다시 동기화"
          >
            {syncMutation.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            동기화
          </Button>
        </div>

        {sorted.length > 0 ? (
          <div className="relative">
            <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.currentTarget.blur();
                  setQuery('');
                }
              }}
              placeholder="워크북 / 데이터원본 검색…"
              className="pl-8 pr-8 h-9 text-sm"
            />
            {query.length > 0 ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="검색 초기화"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
        ) : null}

        {isLoading ? (
          <p className="text-sm text-foreground-muted">불러오는 중…</p>
        ) : error ? (
          <ListError error={error} />
        ) : visible.length === 0 ? (
          trimmedQuery.length > 0 ? (
            <p className="text-sm text-foreground-muted">
              "{trimmedQuery}" 에 해당하는 자원이 없습니다.
            </p>
          ) : (
            <EmptyState data={data} syncPending={syncMutation.isPending} />
          )
        ) : (
          <ul className="rounded-2xl border border-border bg-surface divide-y divide-border max-h-[calc(100vh-260px)] min-h-[300px] overflow-y-auto">
            {visible.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onSelect(r)}
                  className={cn(
                    'w-full text-left px-3 py-2 hover:bg-surface-muted/40',
                    selected?.id === r.id ? 'bg-surface-muted/50' : '',
                  )}
                >
                  <div className="text-[11px] text-foreground-muted truncate">
                    {r.projectPath || '/'}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-sm font-medium truncate">{r.name}</span>
                    <ResourceTypeBadge type={r.resourceType} />
                    <ExtractBadge type={r.extractType} />
                    {r.integrateStartedAt ? (
                      <Loader2
                        className="size-3 animate-spin text-accent shrink-0"
                        aria-label="통합 진행 중"
                      />
                    ) : r.lastIntegrateError ? (
                      <span title={r.lastIntegrateError}>
                        <AlertCircle
                          className="size-3 text-destructive shrink-0"
                          aria-label="마지막 통합 실패"
                        />
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 text-[11px] text-foreground-muted flex items-center gap-2">
                    {r.integrateStartedAt ? (
                      <span className="text-accent truncate">
                        {r.integrateProgressItem ?? '진행 중…'}
                      </span>
                    ) : r.lastIntegratedAt ? (
                      <>
                        <span>마지막: {formatRelative(r.lastIntegratedAt)}</span>
                        {r.avgDurationMs !== null ? (
                          <span>· avg {(r.avgDurationMs / 1000).toFixed(1)}s</span>
                        ) : null}
                      </>
                    ) : (
                      <span>미통합</span>
                    )}
                    {r.scheduleCron ? <span className="font-mono">· {r.scheduleCron}</span> : null}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-surface p-4">
        {!selected ? (
          <p className="text-sm text-foreground-muted">왼쪽에서 자원을 선택하세요.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-[11px] text-foreground-muted">{selected.projectPath || '/'}</div>
              <h4 className="text-base font-medium">{selected.name}</h4>
              <div className="mt-1 flex items-center gap-2">
                <ResourceTypeBadge type={selected.resourceType} />
                <ExtractBadge type={selected.extractType} />
              </div>
              {selected.extractType === 'flow_output' && selected.upstreamFlowName ? (
                <div className="mt-1 text-[11px] text-foreground-muted">
                  Prep 흐름 <strong className="text-foreground">{selected.upstreamFlowName}</strong>{' '}
                  의 출력
                </div>
              ) : null}
            </div>

            <div>
              <label htmlFor="t-res-desc" className="block text-xs text-foreground-muted mb-1">
                설명 (LLM 컨텍스트로 활용)
              </label>
              <textarea
                id="t-res-desc"
                rows={3}
                className="w-full rounded-md border border-border bg-surface-muted/20 px-3 py-2 text-sm focus-visible:outline-none"
                value={descBuffer}
                onChange={(e) => {
                  setDescBuffer(e.target.value);
                  setDescDirty(true);
                }}
                placeholder="이 자원의 비즈니스 의미·주요 컬럼·주의점 등"
              />
              {descDirty ? (
                <div className="mt-1 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDescBuffer(selected.description ?? '');
                      setDescDirty(false);
                    }}
                  >
                    되돌리기
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() =>
                      descMutation.mutate({
                        r: selected,
                        description: descBuffer.length === 0 ? null : descBuffer,
                      })
                    }
                    disabled={descMutation.isPending}
                  >
                    저장
                  </Button>
                </div>
              ) : null}
            </div>

            <IntegrateStatusBox resource={selected} />

            <div className="flex flex-wrap gap-2">
              {selected.extractType === 'live' && !metadataApiAvailable ? (
                // Metadata API 미가용 → 흐름 출력 자동 판별 불가. 사용자가 직접 시도하면
                // 다운로드 프로브(.hyper 존재 여부)로 통합 가능 여부를 최종 판정한다.
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => integrateMutation.mutate({ r: selected, attemptLive: true })}
                  disabled={integrateMutation.isPending || !!selected.integrateStartedAt}
                  title="실제로는 추출본(.hyper)일 수 있는 라이브 자원을 통합 시도합니다"
                >
                  {integrateMutation.isPending || selected.integrateStartedAt ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Play className="size-4" />
                  )}
                  Live 통합 시도
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => integrateMutation.mutate({ r: selected })}
                  disabled={
                    (selected.extractType !== 'extract' &&
                      selected.extractType !== 'flow_output') ||
                    integrateMutation.isPending ||
                    !!selected.integrateStartedAt
                  }
                  title={
                    selected.extractType === 'live'
                      ? '라이브 방식은 지원하지 않습니다'
                      : selected.integrateStartedAt
                        ? '이미 통합 진행 중입니다'
                        : undefined
                  }
                >
                  {integrateMutation.isPending || selected.integrateStartedAt ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Play className="size-4" />
                  )}
                  통합
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={() => setScheduleOpenFor(selected)}
              >
                <CalendarClock className="size-4" />
                스케줄
              </Button>
              {selected.duckdbDataSourceId ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    navigate({
                      to: '/admin/skills/$id',
                      params: { id: 'new' },
                      search: { dataSourceId: selected.duckdbDataSourceId ?? undefined },
                    })
                  }
                >
                  <Sparkles className="size-4" />
                  Skill 만들기
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (
                    window.confirm(
                      `"${selected.name}" 자원과 통합으로 만든 DuckDB 를 함께 삭제할까요?`,
                    )
                  ) {
                    deleteMutation.mutate(selected);
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="size-4" />
                삭제
              </Button>
            </div>

            {selected.duckdbDataSourceId ? (
              <div className="rounded-md border border-border bg-surface-muted/30 p-3 text-xs">
                <div className="text-foreground-muted">
                  통합 산출 DuckDB 데이터소스가 등록되어 있습니다.
                </div>
                <button
                  type="button"
                  className="mt-1 text-accent hover:underline"
                  onClick={() =>
                    navigate({
                      to: '/admin/data-sources/$id',
                      params: { id: selected.duckdbDataSourceId ?? '' },
                    })
                  }
                >
                  열기 →
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <TableauScheduleDialog
        open={!!scheduleOpenFor}
        initial={scheduleOpenFor?.scheduleCron ?? null}
        onClose={() => setScheduleOpenFor(null)}
        onSave={(cron) => {
          if (!scheduleOpenFor) return;
          scheduleMutation.mutate({ r: scheduleOpenFor, cron });
        }}
      />
    </div>
  );
}

/**
 * 빈 상태 — 우선순위:
 * 1. lastSyncError → 빨간 박스 + 사용자가 동기화 버튼으로만 재시도
 * 2. syncRunning → 진행 메시지 + progressItem + 경과 시간
 * 3. syncPending (mutation 중) → 요청 보내는 중
 * 4. structureSyncedAt 있고 자원 0 → "Tableau 에 자원이 없습니다"
 * 5. structureSyncedAt null → 진행을 곧 시작합니다 안내
 */
function EmptyState({
  data,
  syncPending,
}: {
  data: TableauResourceListResponse | undefined;
  syncPending: boolean;
}) {
  if (data?.lastSyncError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-2">
        <div className="flex items-start gap-2 text-destructive">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">자원 동기화 실패</p>
            <p className="mt-1 text-xs whitespace-pre-wrap break-words">{data.lastSyncError}</p>
          </div>
        </div>
        <p className="text-xs text-foreground-muted">
          오류 원인을 확인한 뒤 우측 상단 <strong>"동기화"</strong> 버튼으로 다시 시도하세요.
        </p>
      </div>
    );
  }

  if (data?.syncRunning) {
    const elapsed = data.syncStartedAt
      ? Math.max(1, Math.floor((Date.now() - new Date(data.syncStartedAt).getTime()) / 1000))
      : null;
    return (
      <div className="space-y-1 text-sm text-foreground-muted">
        <p className="flex items-center gap-2">
          <Loader2 className="size-3.5 animate-spin" />
          Tableau 서버에서 자원 목록을 받아오는 중입니다
          {elapsed !== null ? ` (${elapsed}초 경과)` : null}
        </p>
        {data.syncProgressItem ? (
          <p className="text-xs pl-5 font-mono text-foreground-muted">
            진행: {data.syncProgressItem}
          </p>
        ) : null}
      </div>
    );
  }

  if (syncPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-foreground-muted">
        <Loader2 className="size-3.5 animate-spin" />
        요청을 보내는 중입니다…
      </p>
    );
  }

  if (data?.structureSyncedAt) {
    return (
      <p className="text-sm text-foreground-muted">
        이 데이터소스에 등록된 워크북 / 데이터원본이 없습니다. Tableau 에 자원을 추가한 뒤 "동기화"
        를 눌러주세요.
      </p>
    );
  }

  return (
    <p className="text-sm text-foreground-muted">
      아직 한 번도 동기화하지 않았습니다. 우측 상단 <strong>"동기화"</strong> 버튼을 눌러 자원
      목록을 받아오세요.
    </p>
  );
}

/**
 * 자원 목록 fetch 에러. ApiError (서버 명시적 에러) 와 그 외 (대표적으로 ZodError —
 * client/server schema 불일치) 모두 사용자에게 보이도록.
 *
 * Schema 불일치는 보통 서버는 새 빌드인데 브라우저가 옛 client bundle 캐시 보유.
 * 새로고침 (Cmd+Shift+R) 으로 해결되는 경우가 많아 hint 도 함께 표시.
 */
function ListError({ error }: { error: unknown }) {
  if (error instanceof ApiError) {
    return <p className="text-sm text-destructive">{error.message}</p>;
  }
  const message = error instanceof Error ? error.message : String(error);
  // ZodError 메시지는 길어서 잘라 표시
  const short = message.length > 200 ? `${message.slice(0, 200)}…` : message;
  return (
    <div className="space-y-1">
      <p className="text-sm text-destructive">자원 목록을 불러오지 못했습니다.</p>
      <p className="text-xs text-foreground-muted whitespace-pre-wrap break-words">{short}</p>
      <p className="text-xs text-foreground-muted">
        브라우저 캐시 문제일 수 있습니다 — <strong>Cmd+Shift+R</strong> (Win:{' '}
        <strong>Ctrl+Shift+R</strong>) 로 강제 새로고침해 보세요.
      </p>
    </div>
  );
}

/** 선택된 자원의 통합 진행/에러 박스. idle 일 땐 아무것도 안 보임. */
function IntegrateStatusBox({ resource }: { resource: TableauResource }) {
  if (resource.integrateStartedAt) {
    const elapsed = Math.max(
      1,
      Math.floor((Date.now() - new Date(resource.integrateStartedAt).getTime()) / 1000),
    );
    return (
      <div className="rounded-md border border-accent/30 bg-accent/5 p-3 text-sm space-y-1">
        <div className="flex items-center gap-2 text-accent">
          <Loader2 className="size-3.5 animate-spin shrink-0" />
          <span className="font-medium">{resource.integrateProgressItem ?? '진행 중…'}</span>
        </div>
        <p className="text-xs text-foreground-muted pl-5">{elapsed}초 경과</p>
      </div>
    );
  }
  if (resource.lastIntegrateError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-2">
        <div className="flex items-start gap-2 text-destructive">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">마지막 통합 실패</p>
            <p className="mt-1 text-xs whitespace-pre-wrap break-words">
              {resource.lastIntegrateError}
            </p>
          </div>
        </div>
        <p className="text-xs text-foreground-muted">
          오류 원인을 확인한 뒤 아래 <strong>"통합"</strong> 버튼으로 다시 시도하세요.
        </p>
      </div>
    );
  }
  return null;
}

function ResourceTypeBadge({ type }: { type: 'datasource' | 'workbook' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 h-4 rounded-sm text-[10px] uppercase tracking-wider',
        'bg-surface-muted text-foreground-muted',
      )}
    >
      {type === 'workbook' ? '통합문서' : '데이터원본'}
    </span>
  );
}

function ExtractBadge({ type }: { type: 'extract' | 'live' | 'flow_output' }) {
  const isIntegrable = type === 'extract' || type === 'flow_output';
  const label = type === 'flow_output' ? '흐름 출력' : type;
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 h-4 rounded-sm text-[10px] tracking-wider',
        type === 'flow_output' ? '' : 'uppercase',
        isIntegrable ? 'bg-accent/15 text-accent' : 'bg-surface-muted text-foreground-muted',
      )}
      title={
        type === 'live'
          ? '라이브 자원은 통합 불가'
          : type === 'flow_output'
            ? 'Tableau Prep 흐름 출력 — 통합 가능'
            : undefined
      }
    >
      {label}
    </span>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.max(1, Math.floor(diff / 1000))}초 전`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}분 전`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}시간 전`;
  return `${Math.floor(diff / 86_400_000)}일 전`;
}
